import { useMemo } from 'react';
import {
  GRID_COLS,
  TOTAL_CELLS,
  type TileLayout,
} from '@/pages/home/gameConfig';

interface GameBoardProps {
  layout: TileLayout;
  showingIndex: number;
  isShowing: boolean;
  onTilePress: (index: number) => void;
  isInputEnabled: boolean;
  userSequence: number[];
}

export default function GameBoard({
  layout,
  showingIndex,
  isShowing,
  onTilePress,
  isInputEnabled,
  userSequence,
}: GameBoardProps) {
  // 셀 인덱스(row * cols + col) → 타일 인덱스 매핑
  const tileByCell = useMemo(() => {
    const map = new Map<number, number>();
    layout.forEach((cell, index) => {
      map.set(cell.row * GRID_COLS + cell.col, index);
    });
    return map;
  }, [layout]);

  return (
    <div className="w-full h-full rounded-2xl bg-primary-50 overflow-hidden">
      {/* 보이지 않는 4x8 반응형 Grid + 안전 여백 (화면 높이에 따라 축소) */}
      {/* Grid는 타일 위치(셀 중앙) 계산에만 사용하고, 타일 크기는 --tile-size 변수로 독립 제어 */}
      <div className="w-full h-full grid grid-cols-4 grid-rows-8 p-[var(--board-padding)]">
        {Array.from({ length: TOTAL_CELLS }, (_, cellIndex) => {
          const tileIndex = tileByCell.get(cellIndex);
          if (tileIndex === undefined) {
            return <div key={cellIndex} />;
          }

          const isShowingTile = showingIndex === tileIndex;
          const isPressed = userSequence.includes(tileIndex);

          return (
            <div key={cellIndex} className="flex items-center justify-center">
              <button
                onClick={() => {
                  if (isInputEnabled) {
                    onTilePress(tileIndex);
                  }
                }}
                className={`
                  w-[var(--tile-size)] aspect-square rounded-2xl border-2
                  flex items-center justify-center
                  transition-all duration-200 select-none
                  ${isShowingTile
                    ? 'bg-accent-500 border-accent-400 shadow-[0_0_20px_oklch(var(--accent-500)/0.5)] z-10 animate-tile-glow'
                    : isPressed
                      ? 'bg-primary-500 border-primary-400 scale-105 z-0'
                      : isInputEnabled
                        ? 'bg-white border-primary-300 hover:border-primary-400'
                        : 'bg-white border-primary-200'}
                  ${isInputEnabled && !isShowingTile ? 'active:scale-95 cursor-pointer' : 'cursor-default'}
                `}
                disabled={!isInputEnabled || isShowingTile}
                aria-label={`타일 ${tileIndex + 1}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}